/**
 * MonitoringHelpContent Component
 *
 * Contains comprehensive documentation for the System Monitoring dashboard.
 * Explains metrics, thresholds, and recommended actions.
 */
function MonitoringHelpContent() {
  return (
    <div className="monitoring-help-content">
      <section className="mb-4">
        <h6 className="fw-bold">Overview</h6>
        <p>
          The System Monitoring dashboard provides real-time insights into the
          health and performance of the Serve Sync Tennis application. Data is
          collected in 5-minute aggregates and evaluated against SLA targets to
          detect and alert on issues.
        </p>
      </section>

      <section className="mb-4">
        <h6 className="fw-bold">System Health Metrics</h6>
        <div className="ms-3">
          <p className="mb-2">
            <strong>Overall Health:</strong> Aggregated status based on all
            metrics. Shows healthy (✅), degraded (⚠️), or unhealthy (🔴) based
            on threshold evaluations.
          </p>
          <p className="mb-2">
            <strong>Database Pool Usage:</strong> Percentage of database
            connections in use. Target: &lt;70%. Warning at &gt;70%, critical at
            &gt;90%.
          </p>
          <p className="mb-2">
            <strong>API Response Time:</strong> Average response time across all
            API requests. Target: &lt;500ms. Degraded at &gt;500ms, critical at
            &gt;1000ms.
          </p>
          <p className="mb-2">
            <strong>Error Rate:</strong> Percentage of failed requests. Target:
            &lt;1%. Warning at &gt;1%, critical at &gt;5%.
          </p>
          <p className="mb-2">
            <strong>Uptime:</strong> System availability percentage. SLA Target:
            99.9% uptime. Degraded below 99.9%, critical below 99%.
          </p>
        </div>
      </section>

      <section className="mb-4">
        <h6 className="fw-bold">Performance Metrics</h6>
        <div className="ms-3">
          <p className="mb-2">
            <strong>P50 Latency (Median):</strong> 50% of requests complete
            faster than this time. Good indicator of typical user experience.
          </p>
          <p className="mb-2">
            <strong>P95 Latency:</strong> 95% of requests complete faster than
            this time. Shows worst-case performance for most users.
          </p>
          <p className="mb-2">
            <strong>P99 Latency:</strong> 99% of requests complete faster than
            this time. Indicates tail latency and outlier performance.
          </p>
          <p className="mb-2">
            <strong>Request Volume:</strong> Total number of requests in the
            current time window.
          </p>
        </div>
      </section>

      <section className="mb-4">
        <h6 className="fw-bold">Incident Severity Levels</h6>
        <div className="ms-3">
          <p className="mb-2">
            <strong className="text-danger">CRITICAL:</strong> Severe issue
            requiring immediate attention. System may be experiencing
            significant degradation or outage.
          </p>
          <p className="mb-2">
            <strong className="text-warning">WARNING:</strong> Metric
            approaching threshold. Monitor closely and investigate if
            persistent.
          </p>
          <p className="mb-2">
            <strong className="text-info">INFO:</strong> Informational notice.
            No immediate action required but worth noting.
          </p>
        </div>
      </section>

      <section className="mb-4">
        <h6 className="fw-bold">Status Indicators</h6>
        <div className="ms-3">
          <p className="mb-2">
            <strong>✅ Healthy:</strong> All metrics within target ranges.
            System operating normally.
          </p>
          <p className="mb-2">
            <strong>⚠️ Degraded:</strong> One or more metrics approaching limits
            or slightly out of range. Monitor and investigate.
          </p>
          <p className="mb-2">
            <strong>🔴 Critical:</strong> Metrics exceeded thresholds. Immediate
            investigation and action required.
          </p>
          <p className="mb-2">
            <strong>❓ Unknown:</strong> Unable to determine status. May
            indicate missing data or collection issues.
          </p>
        </div>
      </section>

      <section className="mb-4">
        <h6 className="fw-bold">Actions</h6>
        <div className="ms-3">
          <p className="mb-2">
            <strong>Refresh:</strong> Reload the latest monitoring data from the
            database.
          </p>
          <p className="mb-2">
            <strong>Collect Metrics:</strong> Manually trigger metric collection
            for the last 5 minutes. Useful for immediate data updates.
          </p>
          <p className="mb-2">
            <strong>Evaluate Thresholds:</strong> Manually check current metrics
            against thresholds and create incidents if needed.
          </p>
          <p className="mb-2">
            <strong>View Digest:</strong> Navigate to the digest report showing
            aggregated metrics over a selected time period.
          </p>
        </div>
      </section>

      <section className="mb-4">
        <h6 className="fw-bold">Auto-Resolution</h6>
        <p>
          Incidents are automatically resolved when the triggering metric
          returns to acceptable levels. Resolved incidents remain visible in the
          history for audit and trend analysis.
        </p>
      </section>

      <section>
        <h6 className="fw-bold">Recommended Actions for Common Issues</h6>
        <div className="ms-3">
          <p className="mb-2">
            <strong>High Database Pool Usage:</strong> Check for slow queries,
            connection leaks, or increased traffic. Consider scaling database
            connections.
          </p>
          <p className="mb-2">
            <strong>High Response Time:</strong> Investigate slow endpoints,
            database queries, or external service calls. Check server resources.
          </p>
          <p className="mb-2">
            <strong>High Error Rate:</strong> Review application logs for error
            patterns. Check for code issues or external service failures.
          </p>
          <p className="mb-2">
            <strong>Low Uptime:</strong> Investigate recent outages or restarts.
            Check server health and application logs.
          </p>
        </div>
      </section>
    </div>
  )
}

export default MonitoringHelpContent
